/**
 *
 * @module fairPlayDrmProvider
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md#fairplay
 * @see https://github.bamtech.co/services-commons/mdrm/blob/master/FairPlayStreaming_PG.pdf
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import DrmManager from './drmManager';
import DrmProvider from './drmProvider';
import FairPlayCertificateStorage from './fairPlayCertificateStorage';

const Util = Services.Util;
const TokenManager = Token.TokenManager;
const DrmType = Services.Media.DrmType;
const DrmClientEndpoint = Services.Drm.DrmClientEndpoint;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class FairPlayDrmProvider
 * @desc FairPlay is the Apple content encryption standard.
 * @since 3.2.0
 *
 */
export default class FairPlayDrmProvider extends DrmProvider {

    /**
     *
     * @param {SDK.Drm.DrmManager} provider
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(provider, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'FairPlayDrmProvider(provider, tokenManager, logger)',
            get provider() {
                return `${this.ctor} needs valid provider SDK.Drm.DrmManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(provider, DrmManager, errorMessage.provider, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        super(provider, tokenManager, logger);

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.licenseRequestUri = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.fairPlayLicense].href;
        }, '');

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.licenseRequestHeaders = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.fairPlayLicense].headers;
        }, {});

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.certificateUri = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.fairPlayCertificate].href;
        }, '');

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.certificateHeaders = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.fairPlayCertificate].headers;
        }, {});

        /**
         *
         * @access private
         * @type {String}
         * @desc DRM identifier String
         *
         */
        this.type = DrmType.FAIRPLAY;

        /**
         *
         * @access private
         * @type {FairPlayCertificateStorage}
         * @desc references the Storage API to get/set a certificate
         *
         */
        this.certificateStorage = new FairPlayCertificateStorage(
            Util.getSafe(() => this.provider.clientId),
            Util.getSafe(() => this.provider.environment),
            Util.getSafe(() => this.provider.storage),
            this.logger
        );

        this.logger.log(FairPlayDrmProvider.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {Array<Byte>} serverPlaybackContext
     * @param {String} [serverContext=null]
     * @desc Gets a license required for decrypting FairPlay protected content.
     * @emits {MediaFailure} - Raised when the method fails, describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getFairPlayLicense(serverPlaybackContext, serverContext = null) {

        const errorMessage = {
            method: 'FairPlayDrmProvider.getFairPlayLicense(serverPlaybackContext, serverContext)',
            get serverPlaybackContext() {
                return `${this.method} serverPlaybackContext should be an ArrayBuffer view`;
            },
            get serverContext() {
                return `${this.method} serverContext should be a String with length`;
            }
        };

        Check.assert(ArrayBuffer.isView(serverPlaybackContext), errorMessage.serverPlaybackContext, InvalidArgumentException); // eslint-disable-line

        if (Check.assigned(serverContext)) {
            nonEmptyString(serverContext, errorMessage.serverContext, InvalidArgumentException);
        }

        const { provider, logger, failureEvent } = this;

        return provider.getFairPlayLicense(serverPlaybackContext, serverContext).then((data) => {

            return Promise.resolve(data);

        }).catch((exception) => {

            logger.error(FairPlayDrmProvider.name, 'FairPlay license request failure.');
            logger.info(FairPlayDrmProvider.name, `Dispatch ${failureEvent} event.`);

            this.emit(failureEvent, exception);

            return Promise.reject(exception);

        });

    }

    /**
     *
     * @access private
     * @desc Gets a certificate required for decrypting FairPlay protected content. First we attempt to retreive the
     * certificate from storage, if that does not exist we make a new request for a new certificate, store that and
     * then return the certificate.
     * @emits {MediaFailure} - Raised when the method fails, describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getFairPlayCertificate() {

        const { provider, logger, failureEvent, certificateStorage } = this;

        return certificateStorage.getStoredCertificate().then((storedCertificate) => {

            if (Check.assigned(storedCertificate)) {

                return Promise.resolve(storedCertificate);

            } else {

                return provider.getFairPlayCertificate().then((certificate) => {

                    return certificateStorage.storeCertificate(certificate).then(() => {
                        return certificateStorage.getStoredCertificate();
                    });

                }).catch((exception) => {

                    logger.error(FairPlayDrmProvider.name, 'FairPlay certificate request failure.');
                    logger.info(FairPlayDrmProvider.name, `Dispatch ${failureEvent} event.`);

                    this.emit(failureEvent, exception);

                    return Promise.reject(exception);

                });
            }

        });

    }

}



// WEBPACK FOOTER //
// ./src/drm/fairPlayDrmProvider.js